import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { Accordion, AccordionTab } from "primereact/accordion";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import { type TableDataRow, getPerformanceTableColumns } from "./performanceChartModel";
import PerformanceTablePanel from "./PerformanceTablePanel";
import PerformanceChartPanel, { type PerformanceAssetLineChartData } from "./PerformanceChartPanel";
import InterestCreditsPanel from "./InterestCreditsPanel";
import CollateralGapChart, { type CollateralGapBarRow } from "./CollateralGapChart";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";

// ── API filter ids (must match backend) ───────────────────────────────────────

const PERFORMANCE_API_FILTERS = [
  "ACCOUNT_VALUE",
  "SURRENDER_VALUE",
  "DEATH_BENEFIT",
  "INCOME",
  "FINANCING",
] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

type ValueMetricId =
  | "accountValue"
  | "surrenderValue"
  | "deathBenefit"
  | "income"
  | "financing"
  | "collateralGap";

interface MetricBlock {
  projected: number | null;
  actual: number | null;
  trueIQIndex?: number | null;
}

interface IncomeBlock {
  annualIncome: number;
  totalIncome: number;
}

interface CollateralGapBlock {
  difference?: number | null;
  status?: string | null;
  percentage?: number | null;
  surrenderValue?: number | null;
  outsideAssets?: number | null;
  financingBalance?: number | null;
}

interface GraphDataRow {
  planYear: number;
  year: number;
  insuredAge: number;
  interestCredits: number;
  accountValue: MetricBlock;
  align: MetricBlock;
  surrenderValue: MetricBlock;
  deathBenefit: MetricBlock;
  income: IncomeBlock;
  financing: MetricBlock;
  collateralGap?: CollateralGapBlock;
}

interface PerformanceApiData {
  graphData: GraphDataRow[];
  tableData?: TableDataRow[];
}

interface InsuredOption {
  id: string;
  name: string;
}

const PRIMARY = "#004781";

const VALUE_METRICS: { id: ValueMetricId; label: string; color?: string }[] = [
  { id: "accountValue", label: "Account Value", color: `${PRIMARY}99` },
  { id: "surrenderValue", label: "Surrender Value", color: `${PRIMARY}66` },
  { id: "deathBenefit", label: "Death Benefit", color: `${PRIMARY}4D` },
  { id: "income", label: "Income", color: `${PRIMARY}33` },

  { id: "financing", label: "Financing", color: "#757575" },
  { id: "collateralGap", label: "Collateral Gap" },
];

// ── Accordion icons ───────────────────────────────────────────────────────────

const accordionHeader = (title: string) => (
  <div className="text-lg conthrax-font ml-2 text-color-black">{title}</div>
);

const collapseIcon = (
  <div className="rounded-full bg-[#EBEBEF] p-[1px] cursor-pointer">
    <GoTriangleDown className="text-[#8A8AA3]" size={27} style={{ transition: "transform 0.2s" }} />
  </div>
);

const expandIcon = (
  <div className="rounded-full bg-[#EBEBEF] p-[1px] cursor-pointer">
    <GoTriangleUp
      className="text-[#8A8AA3]"
      size={27}
      style={{ transition: "transform 0.2s", rotate: "140deg" }}
    />
  </div>
);

// Renders TrueIqIcon as an ECharts-compatible image:// data URL with the given fill color.
const makeIqSymbol = (color: string): string => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 54 49" fill="none">` +
    `<path d="M31.6871 44.3324C30.7013 42.9642 29.1175 42.1548 27.4314 42.1548H18.4944C17.0884 42.1422 15.7693 41.379 15.0523 40.1663L6.87847 26.2902C6.13208 25.0257 6.13208 23.4545 6.87847 22.19L13.5974 10.7841H6.26091L1.4311 18.9818C-0.478985 22.225 -0.478985 26.2552 1.4311 29.4984L9.60636 43.3759C11.431 46.4707 14.7821 48.4186 18.367 48.4732C18.4426 48.476 18.5 48.476 18.5322 48.476H34.6727L31.6857 44.3324H31.6871ZM52.2387 18.9804L44.0634 5.10289C42.2094 1.95489 38.7897 0 35.1376 0H26.4414L22.6941 6.32119H35.1376C36.5618 6.32119 37.8949 7.08438 38.6189 8.31109L46.7941 22.1872C47.5391 23.4517 47.5391 25.0229 46.7941 26.2874L40.9365 35.2259L44.611 41.4448L52.2401 29.4956C54.1516 26.2524 54.1516 22.2222 52.2401 18.979L52.2387 18.9804ZM9.60776 5.10289L8.88938 6.32119H16.216L19.92 0H18.535C14.8829 0 11.4632 1.95489 9.60776 5.10289Z" fill="${color}"/>` +
    `<path d="M36.7396 22.8095L36.4133 22.2451L36.6556 22.6554C36.685 22.7058 36.7144 22.7576 36.7396 22.8095Z" fill="${color}"/>` +
    `<path d="M48.5281 46.5617H44.2304C43.1298 46.5617 42.0921 46.0267 41.4535 45.1305L33.5892 34.1055C32.5081 32.5904 30.752 31.6857 28.891 31.6843C26.6532 31.6843 23.4128 31.6843 23.2223 31.6843C22.7238 31.6773 22.2575 31.4071 22.004 30.9757L18.4654 24.9696C18.1993 24.5187 18.1993 23.96 18.464 23.5105L22.004 17.5015C22.2617 17.0646 22.7364 16.793 23.242 16.793H30.4314C30.9369 16.793 31.4116 17.0646 31.6693 17.5015L34.9629 23.0932L48.5281 46.5631V46.5617Z" fill="${color}"/>` +
    `</svg>`;
  return `image://data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// ── Page ──────────────────────────────────────────────────────────────────────

function Performance() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const planId = getSessionStore("planInfo")?.planId;

  const proposalId = planId;

  const [insuredOptions, setInsuredOptions] = useState<InsuredOption[]>([]);
  const [insuredLoading, setInsuredLoading] = useState(false);

  useEffect(() => {
    if (!planId) return;
    setInsuredLoading(true);
    makeRequest("reviewCenterReview")
      .get(planId, true)
      .then((res: any) => {
        const list: any[] = res?.data?.data?.insuredList ?? [];
        setInsuredOptions(
          list.map((item: any) => ({
            id: item.userId,
            name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || "Insured",
          })),
        );
      })
      .catch(() => {})
      .finally(() => setInsuredLoading(false));
  }, [planId]);

  const [selectedInsuredId, setSelectedInsuredId] = useState<Set<string>>(new Set());

  const [trueIqIndexOn, setTrueIqIndexOn] = useState(false);
  const [trueIqAlign, setTrueIqAlign] = useState(false);

  const [activeValueFilters, setActiveValueFilters] = useState<Set<ValueMetricId>>(
    new Set(VALUE_METRICS.filter((m) => m.id !== "collateralGap").map((m) => m.id)),
  );
  const toggleValueFilter = (id: ValueMetricId) => {
    // When Align is on, only accountValue is allowed
    if (trueIqAlign && id !== "accountValue") return;
    setActiveValueFilters((prev) => {
      if (id === "collateralGap") {
        if (prev.has("collateralGap")) {
          return new Set(VALUE_METRICS.filter((m) => m.id !== "collateralGap").map((m) => m.id));
        }
        return new Set<ValueMetricId>(["collateralGap"]);
      }
      // When TrueIQ Index is on, Values act as radio buttons (single select)
      if (trueIqIndexOn) {
        return new Set<ValueMetricId>([id]);
      }
      const next = new Set(prev);
      next.delete("collateralGap");
      if (next.has(id)) {
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAlignToggle = () => {
    if (!trueIqAlign) {
      setActiveValueFilters(new Set<ValueMetricId>(["accountValue"]));
    }
    setTrueIqAlign((v) => !v);
  };

  // Align is disabled when non-accountValue metrics are active

  // When Index turns on, collapse to single selection
  const prevTrueIqRef = useRef(trueIqIndexOn);
  useEffect(() => {
    if (trueIqIndexOn && !prevTrueIqRef.current) {
      setActiveValueFilters((prev) => {
        const nonCG = [...prev].filter((id) => id !== "collateralGap");
        return new Set<ValueMetricId>([nonCG[0] ?? "accountValue"]);
      });
    }
    prevTrueIqRef.current = trueIqIndexOn;
  }, [trueIqIndexOn]);

  // Reset TrueIQ toggles when collateral gap mode is active
  useEffect(() => {
    if (activeValueFilters.size === 1 && activeValueFilters.has("collateralGap")) {
      setTrueIqIndexOn(false);
      setTrueIqAlign(false);
    }
  }, [activeValueFilters]);

  const [assetChartFilter, setAssetChartFilter] = useState<string>("year");

  const [selectedYear, setSelectedYear] = useState<number>(getSessionStore("ircSelectedYear") ?? 1);
  const [creditType, setCreditType] = useState<string>(
    getSessionStore("ircSelectedCreditType") ?? "preCredit",
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const newYear =
        detail.year !== undefined
          ? detail.year
          : getSessionStore("ircSelectedYear") ?? selectedYear;
      const newCreditType =
        detail.creditType !== undefined
          ? detail.creditType
          : getSessionStore("ircSelectedCreditType") ?? creditType;
      setSelectedYear(newYear);
      setCreditType(newCreditType);
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  const [graphData, setGraphData] = useState<GraphDataRow[]>([]);
  const [tableData, setTableData] = useState<TableDataRow[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState<string | null>(null);

  useEffect(() => {
    if (!insuredOptions.length || selectedInsuredId.size > 0) return;
    setSelectedInsuredId(new Set([insuredOptions[0].id]));
  }, [insuredOptions, selectedInsuredId]);

  const fetchPerformance = useCallback(async () => {
    if (!selectedInsuredId.size) return;
    setPerformanceLoading(true);
    setPerformanceError(null);
    const isCGMode = activeValueFilters.size === 1 && activeValueFilters.has("collateralGap");
    const filters = isCGMode ? ["COLLATERAL_GAP"] : [...PERFORMANCE_API_FILTERS];
    const filtersParam = encodeURIComponent(JSON.stringify(filters));
    const insuredIdsParam = encodeURIComponent(JSON.stringify([...selectedInsuredId]));
    const query = `filters=${filtersParam}&year=${selectedYear}&insuredIds=${insuredIdsParam}&creditType=${creditType}`;
    try {
      const res = await makeRequest("interactiveReviewCenterPerformance").get(
        `${proposalId}?${query}`,
        true,
      );
      const data = res?.data?.data as PerformanceApiData | undefined;
      setGraphData(Array.isArray(data?.graphData) ? data!.graphData : []);
      setTableData(Array.isArray(data?.tableData) ? data!.tableData! : []);
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? "Failed to load performance data";
      setPerformanceError(msg);
      setGraphData([]);
    } finally {
      setPerformanceLoading(false);
    }
  }, [proposalId, selectedYear, creditType, [...selectedInsuredId].join(","), activeValueFilters]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const [showProjections, setShowProjections] = useState(true);
  const showProjectedSeries = showProjections;
  const trueIqShadingOn = trueIqIndexOn || trueIqAlign;

  const insuredName =
    insuredOptions
      .filter((o) => selectedInsuredId.has(o.id))
      .map((o) => o.name)
      .join(", ") || "Insured";

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const divider = isDark ? "border-[#3A3A50]" : "border-gray-100";
  const textSub = isDark ? "text-gray-400" : "text-[#55556D]";

  const chartBusy = insuredLoading || performanceLoading;

  // const PRIMARY = "#1E3A8A";

  const performanceChartHeader = (
    <div className="flex items-center justify-between w-full pr-2">
      <div className="text-lg conthrax-font ml-2 text-color-black dark:!text-gray-100">
        Performance Chart
      </div>
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <AnimatedCheckbox
          checked={showProjections}
          onChange={() => setShowProjections((v) => !v)}
          inputId="performance-projections"
          label={
            <span className="flex items-center gap-1">
              <span style={{ width: 39, height: 36, display: "inline-flex", flexShrink: 0 }}>
                <ProjectionsIcon
                  fill={showProjections ? PRIMARY : isDark ? "#6B7280" : "#9CA3AF"}
                />
              </span>
            </span>
          }
          brandingColor={PRIMARY}
          size={20}
          hideBorder
        />
      </div>
    </div>
  );

  const assetLineChartData = useMemo<PerformanceAssetLineChartData>(
    () =>
      graphData.map((row) => ({
        year: row?.year,
        planYear: row?.planYear,
        insuredAge: row?.insuredAge,
        accountValue: row?.accountValue?.actual ?? null,
        projectedAccountValue: row?.accountValue?.projected ?? null,
        surrenderValue: row?.surrenderValue?.actual ?? null,
        projectedSurrenderValue: row?.surrenderValue?.projected ?? null,
        benefit: row?.deathBenefit?.actual ?? null,
        projectedBenefit: row?.deathBenefit?.projected ?? null,
        financing: row?.financing?.actual ?? null,
        projectedFinancing: row?.financing?.projected ?? null,
      })),
    [graphData],
  );

  const isCollateralGapMode =
    activeValueFilters.size === 1 && activeValueFilters.has("collateralGap");

  const collateralGapBarData = useMemo<CollateralGapBarRow[]>(() => {
    const toNum = (v: number | null | undefined): number | null => {
      if (v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return graphData
      .map((row) => {
        const coll = row.collateralGap as CollateralGapBlock | undefined;
        return {
          planYear: row.planYear,
          year: row.year,
          insuredAge: row.insuredAge,
          surrenderValue: toNum(coll?.surrenderValue),
          outsideAssets: toNum(coll?.outsideAssets),
          financingBalance: toNum(coll?.financingBalance),
          difference: toNum(coll?.difference),
          status: coll?.status ?? null,
          percentage: toNum(coll?.percentage),
        };
      })
      .filter(
        (r) => r.surrenderValue != null || r.outsideAssets != null || r.financingBalance != null,
      );
  }, [graphData]);

  const chartSeriesFilters = useMemo(() => [...activeValueFilters], [activeValueFilters]);

  const trueIqSourceMetric = useMemo<ValueMetricId>(() => {
    const nonCG = [...activeValueFilters].filter((id) => id !== "collateralGap");
    return (nonCG[0] ?? "accountValue") as ValueMetricId;
  }, [activeValueFilters]);

  const { trueIqAppendSeries, combinedFirstLineExtras, chartGridTop } = useMemo(() => {
    const rows = graphData;
    if (!rows.length || (!trueIqIndexOn && !trueIqAlign)) {
      return {
        trueIqAppendSeries: [] as object[],
        combinedFirstLineExtras: undefined as Record<string, unknown> | undefined,
        chartGridTop: "5%",
      };
    }

    const toNum = (v: unknown): number | null => {
      if (v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const pickRow = (
      row: GraphDataRow,
    ): { actual: number | null; projected: number | null; iq: number | null } => {
      switch (trueIqSourceMetric) {
        case "surrenderValue":
          return {
            actual: toNum(row.surrenderValue?.actual),
            projected: toNum(row.surrenderValue?.projected),
            iq: toNum(row.surrenderValue?.trueIQIndex),
          };
        case "deathBenefit":
          return {
            actual: toNum(row.deathBenefit?.actual),
            projected: toNum(row.deathBenefit?.projected),
            iq: toNum(row.deathBenefit?.trueIQIndex),
          };
        case "financing":
          return {
            actual: toNum(row.financing?.actual),
            projected: toNum(row.financing?.projected),
            iq: toNum(row.financing?.trueIQIndex),
          };
        case "income":
          return {
            actual: toNum(row.income?.totalIncome),
            projected: toNum(row.income?.annualIncome),
            iq: null,
          };
        default:
          return {
            actual: toNum(row.accountValue?.actual),
            projected: toNum(row.accountValue?.projected),
            iq: toNum(row.accountValue?.trueIQIndex),
          };
      }
    };

    const actualLine = rows.map((r) => pickRow(r).actual);
    const projectedLine = rows.map((r) => pickRow(r).projected);
    const indexByPoint = rows.map((r) => pickRow(r).iq);

    const yDisp: (number | null)[] = rows.map((_, i) => {
      const a = actualLine[i];
      if (a != null) return a;
      if (showProjectedSeries) {
        const p = projectedLine[i];
        if (p != null) return p;
      }
      return null;
    });

    const greenGrad = {
      type: "linear" as const,
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "rgba(34,197,94,0.70)" },
        { offset: 1, color: "rgba(34,197,94,0.30)" },
      ],
    };
    const redGrad = {
      type: "linear" as const,
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: "rgba(220,38,38,0.70)" },
        { offset: 1, color: "rgba(220,38,38,0.30)" },
      ],
    };

    // null IQ → transparent fill so original blue shows through
    const fillFor = (iq: number | null) =>
      iq == null ? "rgba(0,0,0,0)" : iq >= 100 ? greenGrad : redGrad;
    // null IQ → no line run (original line stays); non-null → green or red
    const lineColorFor = (iq: number): string => (iq < 100 ? "#dc2626" : "#22c55e");

    const segIq = (i: number): number | null => {
      const end = indexByPoint[i + 1];
      if (end != null) return end;
      return indexByPoint[i];
    };

    type FillSeg = [number, number, number, number | null];
    const fillSegs: FillSeg[] = [];
    for (let i = 0; i < rows.length - 1; i++) {
      const y0 = yDisp[i];
      const y1 = yDisp[i + 1];
      // Only add fill segment when IQ is non-null (null → transparent, no need to draw)
      const iq = segIq(i);
      if (y0 != null && y1 != null && iq != null) fillSegs.push([i, y0, y1, iq]);
    }

    type LineRun = { start: number; end: number; color: string };
    const lineRuns: LineRun[] = [];
    for (let i = 0; i < rows.length - 1; i++) {
      const y0 = yDisp[i];
      const y1 = yDisp[i + 1];
      const iq = segIq(i);
      // Skip null IQ segments — original blue line shows through
      if (y0 != null && y1 != null && iq != null) {
        const c = lineColorFor(iq);
        const last = lineRuns[lineRuns.length - 1];
        if (last && last.color === c && last.end === i) last.end = i + 1;
        else lineRuns.push({ start: i, end: i + 1, color: c });
      }
    }

    const append: object[] = [];

    // Fill polygons and colored line runs only shown in Index mode, not Align-only mode
    if (trueIqIndexOn && fillSegs.length) {
      append.push({
        name: "TrueIQ fills",
        type: "custom",
        coordinateSystem: "cartesian2d",
        z: 3,
        silent: true,
        clip: true,
        animation: false,
        tooltip: { show: false },
        data: fillSegs,
        renderItem(
          _p: unknown,
          api: {
            value: (d: number) => unknown;
            coord: (v: [number, number]) => number[] | undefined;
          },
        ) {
          const i = api.value(0) as number;
          const y0 = api.value(1) as number;
          const y1 = api.value(2) as number;
          const iq = api.value(3) as number | null;
          const bl = api.coord([i, 0]);
          const tl = api.coord([i, y0]);
          const tr = api.coord([i + 1, y1]);
          const br = api.coord([i + 1, 0]);
          if (!bl || !tl || !tr || !br) return null;
          return {
            type: "polygon",
            shape: { points: [bl, tl, tr, br] },
            style: { fill: fillFor(iq) },
          };
        },
      });
    }

    trueIqIndexOn &&
      lineRuns.forEach((run, idx) => {
        append.push({
          name: `TrueIQ Line ${idx}`,
          type: "line",
          silent: true,
          clip: true,
          z: 4,
          connectNulls: false,
          showSymbol: false,
          tooltip: { show: false },
          lineStyle: { color: run.color, width: 2.5 },
          itemStyle: { color: run.color },
          data: rows.map((_, k) => {
            if (k < run.start || k > run.end) return null;
            const y = yDisp[k];
            return y != null ? y : null;
          }),
        });
      });

    type IqDatum = { value: [number, number]; iqVal: string; iqOk: boolean };
    const scatterData: IqDatum[] = rows
      .map((_, i) => {
        const iq = indexByPoint[i];
        if (iq == null) return null;
        const y = yDisp[i];
        if (y == null) return null;
        return { value: [i, y] as [number, number], iqVal: iq.toFixed(1), iqOk: iq >= 100 };
      })
      .filter((x): x is IqDatum => x != null);

    if (!trueIqAlign && scatterData.length) {
      append.push({
        name: "TrueIQ",
        type: "scatter",
        z: 20,
        clip: false,
        silent: true,
        tooltip: { show: false },
        symbolSize: [35, 32] as [number, number],
        symbolKeepAspect: true,
        symbolOffset: [40, -40],
        emphasis: { disabled: true },
        labelLayout: { hideOverlap: false },
        data: scatterData.map((d) => {
          const color = d.iqOk ? (isDark ? "#86efac" : "#15803d") : isDark ? "#fca5a5" : "#b91c1c";
          return {
            value: d.value,
            symbol: makeIqSymbol(color),
            itemStyle: { opacity: 0.95 },
            label: {
              show: true,
              position: "bottom",
              distance: 4,
              offset: [0, 6],
              align: "center",
              verticalAlign: "top",
              overflow: "none" as const,
              formatter: d.iqVal,
              color,
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "system-ui, sans-serif",
              lineHeight: 16,
            },
          };
        }),
      });
    }

    // ── Align mode: vertical connectors + icon markers ───────────────────────
    if (trueIqAlign) {
      const ALIGN_ANCHOR_ACTUAL_ONLY = false;

      type ConnectorDatum = [number, number, number];
      const connectorData: ConnectorDatum[] = [];
      const iconData: {
        value: [number, number];
        symbol: string;
        label: object;
        acIQ: number | null;
        alignIQ: number | null;
        interestCredits: number | null;
        planYear: number;
        year: number;
        insuredAge: number;
      }[] = [];
      rows.forEach((r, i) => {
        if (!showProjectedSeries && toNum(r.accountValue?.actual) == null) return;
        const rawAnchorY = ALIGN_ANCHOR_ACTUAL_ONLY
          ? toNum(r.accountValue?.actual)
          : toNum(r.accountValue?.actual) ?? toNum(r.accountValue?.projected);
        if (rawAnchorY == null) return;

        const interestCreditsZero = !r.interestCredits;
        const alignActual = toNum(r.align?.actual);
        const iconY = !interestCreditsZero && alignActual != null ? alignActual : rawAnchorY;

        if (Math.abs(iconY - rawAnchorY) > 0) {
          connectorData.push([i, iconY, rawAnchorY]);
        }

        const alignIq = toNum(r.align?.trueIQIndex);
        const iqOk = alignIq != null && alignIq >= 100;

        const iconColor = trueIqIndexOn
          ? iqOk
            ? isDark
              ? "#86efac"
              : "#22c55e"
            : isDark
            ? "#fca5a5"
            : "#dc2626"
          : "#004781";

        const label =
          trueIqIndexOn && alignIq != null
            ? {
                show: true,
                position: "bottom",
                distance: 4,
                offset: [0, 6],
                align: "center",
                verticalAlign: "top",
                formatter: alignIq.toFixed(1),
                color: iqOk ? (isDark ? "#86efac" : "#15803d") : isDark ? "#fca5a5" : "#b91c1c",
                fontSize: 12,
                fontWeight: 800,
                fontFamily: "system-ui, sans-serif",
              }
            : { show: false };

        iconData.push({
          value: [i, iconY],
          symbol: makeIqSymbol(iconColor),
          label,
          acIQ: toNum(r.accountValue?.trueIQIndex),
          alignIQ: alignIq,
          interestCredits: r.interestCredits ?? null,
          planYear: r.planYear,
          year: r.year,
          insuredAge: r.insuredAge,
        });
      });
      // ── end dummy / real data ─────────────────────────────────────────────

      if (connectorData.length) {
        append.push({
          name: "Align Connector",
          type: "custom",
          coordinateSystem: "cartesian2d",
          z: 10,
          silent: true,
          clip: true,
          animation: false,
          tooltip: { show: false },
          data: connectorData,
          renderItem(
            _p: unknown,
            api: {
              value: (d: number) => unknown;
              coord: (v: [number, number]) => number[] | undefined;
            },
          ) {
            const xIdx = api.value(0) as number;
            const iconYVal = api.value(1) as number;
            const anchorYVal = api.value(2) as number;
            const top = api.coord([xIdx, iconYVal]);
            const bot = api.coord([xIdx, anchorYVal]);
            if (!top || !bot) return null;
            return {
              type: "line",
              shape: { x1: top[0], y1: top[1], x2: bot[0], y2: bot[1] },
              style: { stroke: "#004781", lineWidth: 1.5, lineDash: [4, 4], opacity: 0.7 },
            };
          },
        });
      }

      if (iconData.length) {
        append.push({
          name: "Align TrueIQ",
          type: "scatter",
          z: 20,
          clip: false,
          silent: false,
          tooltip: { show: false },
          symbolSize: [22, 22],
          symbolKeepAspect: true,
          emphasis: { disabled: true },
          labelLayout: { hideOverlap: false },
          data: iconData,
        });
      }
    }

    return {
      trueIqAppendSeries: append,
      combinedFirstLineExtras: undefined,
      chartGridTop: !trueIqAlign && scatterData.length ? "10%" : "5%",
    };
  }, [graphData, trueIqIndexOn, trueIqAlign, trueIqSourceMetric, showProjectedSeries, isDark]);

  const tableColumns = useMemo(() => getPerformanceTableColumns(), []);

  const tableRowClassName = useCallback(
    (rowData: TableDataRow): string => (rowData.planYear === selectedYear ? "font-semibold" : ""),
    [selectedYear],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-5 pb-8">
      {!proposalId && (
        <p className={`text-sm ${textSub}`}>
          No proposal is selected. Open this page from a proposal.
        </p>
      )}

      {proposalId && !insuredOptions.length && !insuredLoading && (
        <p className={`text-sm ${textSub}`}>
          No insured persons found on the proposal summary. Insured selection is required for the
          performance API.
        </p>
      )}

      {performanceError && (
        <p className="text-sm text-red-500" role="alert">
          {performanceError}
        </p>
      )}

      {/* ── Insured Person(s) + TrueIQ ──────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p
            className={`text-[18px] conthrax-font font-semibold uppercase tracking-wider mb-2 ${textSub}`}
          >
            Insured Person(s)
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {insuredOptions.map((person) => (
              <AnimatedCheckbox
                key={person.id}
                checked={selectedInsuredId.has(person.id)}
                onChange={
                  () =>
                    setSelectedInsuredId((prev) => {
                      const next = new Set(prev);
                      if (next.has(person.id)) {
                        if (next.size > 1) next.delete(person.id);
                      } else {
                        next.add(person.id);
                      }
                      return next;
                    })
                  // eslint-disable-next-line
                }
                inputId={`insured-${person.id}`}
                label={person.name}
                brandingColor={PRIMARY}
                size={20}
              />
            ))}
          </div>
        </div>

        {/* .TrueIQ */}
        <div
          className={`flex flex-col items-center gap-1.5 px-4 py-2 rounded-md border border-[${PRIMARY}55] border-[1px] ${
            isDark ? `text-[#93C5FD] bg-[${PRIMARY}15]` : `text-[${PRIMARY}] bg-[${PRIMARY}08]`
          }`}
        >
          <div className="flex flex-col items-start gap-2">
            <div className="flex gap-1 items-center mx-auto">
              <TrueIqIcon width={18} fill="#55556D" />
              <span className="text-md conthrax-font text-[#55556D]">TrueIQ</span>
            </div>
            <div className="flex items-center gap-3">
              <AnimatedCheckbox
                checked={trueIqIndexOn}
                onChange={() => setTrueIqIndexOn((v) => !v)}
                inputId="trueiq-index"
                label="Index"
                brandingColor={PRIMARY}
                size={20}
                hideBorder
              />
              <AnimatedCheckbox
                checked={trueIqAlign}
                onChange={handleAlignToggle}
                inputId="trueiq-align"
                label="Align"
                brandingColor={PRIMARY}
                size={20}
                hideBorder
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Values ──────────────────────────────────────────────────────── */}
      <div>
        <p
          className={`text-[18px] font-semibold uppercase conthrax-font tracking-wider mb-2 ${textSub}`}
        >
          Values
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {VALUE_METRICS.map((m) => (
            <AnimatedCheckbox
              key={m.id}
              checked={activeValueFilters.has(m.id)}
              onChange={() => toggleValueFilter(m.id)}
              inputId={`value-${m.id}`}
              isDisabled={trueIqAlign && m.id !== "accountValue"}
              label={
                <span className="flex items-center gap-2">
                  {m.label}
                  <span
                    className="w-[12px] h-[12px] rounded-full inline-block"
                    style={{ backgroundColor: m.color }}
                  />
                </span>
              }
              brandingColor={PRIMARY}
              size={20}
              borderExtra="1px solid #EBEBEF"
            />
          ))}
        </div>
      </div>

      {/* ── Accordion ───────────────────────────────────────────────────── */}
      <Accordion
        collapseIcon={collapseIcon}
        expandIcon={expandIcon}
        multiple
        activeIndex={[0, 1, 2]}
        className="bg-gray-100 dark:!bg-[#111111] rounded-md ip-project-accordion-main"
      >
        <AccordionTab
          headerTemplate={performanceChartHeader}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          {isCollateralGapMode ? (
            <div
              className={`rounded-2xl overflow-hidden border shadow-sm ${
                isDark ? "border-[#3A3A50] shadow-none" : "border-slate-200/90 shadow-md"
              } ${cardBg} px-4 pt-4 pb-2`}
            >
              <CollateralGapChart data={collateralGapBarData} isDark={isDark} loading={chartBusy} />
            </div>
          ) : (
            <PerformanceChartPanel
              isDark={isDark}
              cardBg={cardBg}
              divider={divider}
              textSub={textSub}
              insuredName={insuredName}
              chartBusy={chartBusy}
              graphDataLength={graphData.length}
              performanceLoading={performanceLoading}
              assetLineChartData={assetLineChartData}
              assetChartFilter={assetChartFilter}
              onAssetChartFilterChange={setAssetChartFilter}
              chartSeriesFilters={chartSeriesFilters}
              showProjections={showProjectedSeries}
              onProjectionsToggle={() => {}}
              combinedFirstLineExtras={combinedFirstLineExtras}
              trueIqAppendSeries={trueIqAppendSeries}
              chartGridTop={chartGridTop}
              trueIqChartMode={trueIqShadingOn}
              tooltipFormatterOverride={trueIqAlign ? () => "" : null}
            />
          )}
        </AccordionTab>

        <AccordionTab
          headerTemplate={accordionHeader("Interest Credits")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <InterestCreditsPanel
            isDark={isDark}
            cardBg={cardBg}
            divider={divider}
            graphData={graphData as any}
            tableData={tableData}
          />
        </AccordionTab>

        <AccordionTab
          headerTemplate={accordionHeader("Performance Table")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <PerformanceTablePanel
            isDark={isDark}
            cardBg={cardBg}
            textSub={textSub}
            chartBusy={chartBusy}
            columns={tableColumns}
            rows={tableData}
            rowClassName={tableRowClassName}
            activeValueFilters={activeValueFilters}
          />
        </AccordionTab>
      </Accordion>
    </div>
  );
}

export default Performance;
